import {
  AccountAssetsListItem,
  type AccountType,
  type AssetGroup,
  type AssetRests,
  type GetAssetsActionButtonsVisibility,
  type MarginAssetRests,
  type MarginIndicators,
} from '@/entities/account';
import type { BaseAsset } from '@/shared/types';
import { cn } from '@/shared/utils/styling';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

type Props = {
  isBalancesHidden: boolean;
  isLowBalancesHidden: boolean;
  baseLowBalance: number;
  type: AccountType;
  typeName?: string;
  accountNumber: string;
  tradingAccKey?: number;
  accountAssets: Array<AssetRests | MarginAssetRests>;
  assetsIcons: Record<string, string> | undefined;
  baseAsset: BaseAsset;
  basePrecission: number;
  getButtonsVisibility: GetAssetsActionButtonsVisibility;
  className?: string;
  openDepositModal: (props?: {
    assetGroup: AssetGroup;
    assetCode?: string;
  }) => void;
  openWithdrawModal: (props?: {
    assetGroup: AssetGroup;
    assetCode?: string;
  }) => void;
  openInternalTransferFormModal: (props?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction: 'from' | 'to';
  }) => void;
  openVerificationInfoModal: () => void;
  isVerified: boolean;
  marginIndicators?: MarginIndicators;
  leverage?: string;
};

const AccountAssetsList = ({
  isBalancesHidden,
  isLowBalancesHidden,
  baseLowBalance,
  type,
  typeName,
  accountNumber,
  tradingAccKey,
  accountAssets,
  assetsIcons,
  baseAsset,
  basePrecission,
  getButtonsVisibility,
  className,
  openDepositModal,
  openWithdrawModal,
  openInternalTransferFormModal,
  openVerificationInfoModal,
  isVerified,
  marginIndicators,
  leverage,
}: Props) => {
  const navigate = useNavigate();

  const showHeader = typeName || (tradingAccKey && type !== 'ewallet');

  const onExchangeClick = (assetCode: string) => {
    if (!isVerified) {
      openVerificationInfoModal();
      return;
    }
    navigate('/exchange', {
      state: {
        asset: assetCode,
      },
    });
  };

  const assets = useMemo(() => {
    if (!isLowBalancesHidden) {
      return accountAssets;
    }

    return accountAssets.filter(
      asset =>
        ('baseEquity' in asset
          ? (asset?.baseEquity ?? 0)
          : (asset?.baseAvailable ?? 0)) > baseLowBalance
    );
  }, [isLowBalancesHidden, baseLowBalance, accountAssets]);

  return (
    <div className={cn(className, 'mb-[1.6rem]')}>
      {showHeader && (
        <h4 className="headline4 px-0 md:px-[1.6rem]">
          <span className="text-primary">{typeName ? `${typeName} ` : ''}</span>
          <span className={cn('text-secondary', !typeName && 'text-primary')}>
            {type !== 'ewallet' ? tradingAccKey : ''}
          </span>
        </h4>
      )}

      <div className="flex flex-col">
        {assets?.map(asset => {
          return (
            <AccountAssetsListItem
              isBalancesHidden={isBalancesHidden}
              key={asset.code}
              accountType={type}
              asset={asset}
              icon={assetsIcons?.[asset.code]}
              basePrecission={basePrecission}
              baseAsset={baseAsset}
              getButtonsVisibility={getButtonsVisibility}
              accountNumber={accountNumber}
              tradingAccKey={tradingAccKey}
              marginIndicators={marginIndicators}
              leverage={leverage}
              openDepositModal={() =>
                openDepositModal({
                  assetGroup: asset.group ?? undefined,
                  assetCode: asset.code ?? undefined,
                })
              }
              openWithdrawModal={() =>
                openWithdrawModal({
                  assetGroup: asset.group ?? undefined,
                  assetCode: asset.code ?? undefined,
                })
              }
              openInternalTransferFormModal={openInternalTransferFormModal}
              onExchange={onExchangeClick}
            />
          );
        })}
      </div>
    </div>
  );
};

export { AccountAssetsList };
